<template>
  <div
    :class="[
      'certification-tile',
      {
        'certification-tile--pt-70': isRenewalAvailableHigherCert || isExpired,
        'certification-tile--flex-jc-start': isExpired,
      },
    ]"
  >
    <div
      :class="[
        'certification-tile__status-banner',
        isExpired && 'certification-tile__status-banner--expired',
        isExpiresSoon && 'certification-tile__status-banner--expires-soon',
        isGracePeriod && 'certification-tile__status-banner--grace-period',
        isRenewalAvailable &&
          'certification-tile__status-banner--renew-available',
        isRenewalAvailableHigherCert &&
          'certification-tile__status-banner--renew-ready',
      ]"
    >
      <div v-if="isExpired" class="certification-tile__status-banner--expired">
        Expired
      </div>
      <div
        v-if="isExpiresSoon"
        :class="[
          'certification-tile__status-banner--expires-soon',
          isRenewalAvailableHigherCert &&
            'certification-tile__status-banner--pb-15',
        ]"
      >
        <AlertTriangleIcon class="icon" />
        <span>EXPIRES SOON:</span> Renew to stay valid
      </div>
      <div
        v-if="isGracePeriod"
        :class="[
          'certification-tile__status-banner--grace-period',
          isRenewalAvailableHigherCert &&
            'certification-tile__status-banner--pb-15',
        ]"
      >
        <AlertOctagonIcon class="icon" />
        <span>EXPIRED:</span> {{ `${daysToRenew} days to renew` }}
      </div>
      <div
        v-if="isRenewalAvailable"
        :class="[
          'certification-tile__status-banner--renew-available',
          isRenewalAvailableHigherCert &&
            'certification-tile__status-banner--pb-15',
        ]"
      >
        <AlertCircleIcon class="icon" />
        <span>RENEWAL AVAILABLE</span>
      </div>
      <div
        v-if="isRenewalAvailableHigherCert && !(isExpired || isEarlyStage)"
        class="certification-tile__status-banner--renew-ready"
      >
        You earned a more advanced certification, you are ready to renew.
      </div>
    </div>
    <div
      :class="[
        'certification-tile__badge',
        isExpired && 'certification-tile__badge--expired',
      ]"
    >
      <img
        class="certification-tile__badge-img"
        :alt="`${certification.name} certification badge`"
        :src="certification.logo_image_url"
      />
    </div>
    <div class="certification-tile__title">{{ certification.name }}</div>
    <div class="certification-tile__awarded">
      {{ `Awarded: ${formatDate(certification.awarded)}` }}
    </div>
    <div class="certification-tile__expires">
      {{
        `${isExpired || isGracePeriod ? 'Expired' : 'Expires'}: ${formatDate(
          certification.cert_awarded_expiration_date
        )}`
      }}
    </div>
    <div
      v-if="showCPEExternalCredits"
      class="certification-tile__cpe-credits-external"
    >
      {{ `Additional credits earned: +${cpeExternalCredits}` }}
      <InfoIcon
        v-tooltip="{
          content: 'Credits added to this certification for qualifying achievements',
          placement: 'right-center',
          classes: ['certification-tile__info-tooltip'],
        }"
        class="certification-tile__info-icon"
      />
    </div>
    <div
      v-if="creditsNeeded && showRenewalAction"
      class="certification-tile__cpe-credits-needed"
    >
      {{ `INE CPE credits needed to renew: ${creditsNeeded}` }}
    </div>
    <SeparatorBorder class="certification-tile__separator" />
    <div class="certification-tile__actions">
      <button v-if="attempt" :class="ctaClasses" @click="showAttemptScore">
        Exam results
      </button>
      <template class="certification-tile__show-certification">
        <a
          v-if="isAccredibleEnabled"
          :href="certification.accredible_credential_url"
          target="_blank"
          rel="noopener nofollow"
          :class="ctaClasses"
        >
          {{ goToCert }}
        </a>
        <router-link
          v-else
          :class="ctaClasses"
          :to="{
            name: 'certificate-verified',
            params: {
              attemptId: attempt ? attempt.id : certification.attempt_id,
            },
          }"
        >
          {{ goToCert }}
        </router-link>
      </template>
      <template v-if="isRenewalLoading">
        <ActionLoader
          name="line-scale"
          color="#ef7d22"
          class="certification-tile__renew-certification-spinner"
        />
      </template>
      <template v-else>
        <ModalContainer
          v-if="showRenewalAction"
          class="certification-tile__renew-certification"
          triggerLabel="Renew Certification"
          triggerStyles="btn btn--primary btn--full"
          :showModal="showRenewModal"
          :showCloseBtn="false"
          :focusOutClose="false"
          :customTop="'70px'"
          @open-modal="showRenewModal = true"
          @close-modal="handleCloseRenewalModal"
        >
          <CPERenewModal
            :certification="certification"
            @close-modal="showRenewModal = false"
          />
        </ModalContainer>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { format } from 'date-fns-tz'
import SeparatorBorder from '@/components/shared/SeparatorBorder.vue'
import AlertCircleIcon from '@/assets/icons/ alert-green-icon.svg'
import AlertOctagonIcon from '@/assets/icons/octagon-alert-orange-icon.svg'
import AlertTriangleIcon from '@/assets/icons/triangle-alert-icon.svg'
import { InfoIcon } from 'vue-feather-icons'
import ModalContainer from '@/components/shared/ModalContainer.vue'
import CPERenewModal from '@/components/user-dashboard/cpe-program/RenewModal/RenewModal.vue'
import CONSTANTS from '@/constants'
import { certificationCommons } from '@/helpers/mixins'
import ActionLoader from '@/components/shared/ActionLoader.vue'

const {
  CERTIFICATIONS: {
    RENEWAL_STATUS: {
      EARLY,
      EXPIRED,
      TO_EXPIRE,
      GRACE_PERIOD,
      RENEWAL_AVAILABLE,
    },
  },
} = CONSTANTS

export default {
  name: 'CertificationListItem',
  components: {
    SeparatorBorder,
    ModalContainer,
    CPERenewModal,
    InfoIcon: { ...InfoIcon, name: 'InfoIcon' },
    AlertTriangleIcon: { ...AlertTriangleIcon, name: 'AlertTriangleIcon' },
    AlertOctagonIcon: { ...AlertOctagonIcon, name: 'AlertOctagonIcon' },
    AlertCircleIcon: { ...AlertCircleIcon, name: 'AlertCircleIcon' },
    ActionLoader,
  },
  mixins: [certificationCommons],
  props: {
    certification: {
      type: Object,
      required: true,
    },
    attempt: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      goToCert: 'See certification',
      showRenewModal: false,
      isRenewalLoading: false,
    }
  },
  computed: {
    ...mapGetters('app', ['isAccredibleEnabled']),
    ...mapGetters('cpeProgram', ['cpeCreditHistoryExternal']),
    cpeExternalCredits() {
      if (this.cpeCreditHistoryExternal) {
        const count = this.cpeCreditHistoryExternal
          .filter(
            credit => credit.restricted_to_content_id === this.certification.attempt_id
          )
          .reduce((acc, credit) => acc + credit.credits, 0)
        return count
      }
      return 0
    },
    showCPEExternalCredits() {
      if (this.isExpired) return false
      return this.cpeExternalCredits > 0
    },
    ctaClasses() {
      return [
        'btn',
        'btn--secondary',
        'certification-tile__default-actions',
        this.isExpired && 'certification-tile__default-actions--expired',
      ]
    },
    creditsNeeded() {
      return this.certification.cpe_credits_needed - this.cpeExternalCredits
    },
    showRenewalAction() {
      return (
        this.certification.expiration_status &&
        !(this.isExpired || this.isEarlyStage)
      )
    },
    isEarlyStage() {
      return this.certification.expiration_status === EARLY
    },
    isExpired() {
      return this.certification.expiration_status === EXPIRED
    },
    isExpiresSoon() {
      return this.certification.expiration_status === TO_EXPIRE
    },
    isGracePeriod() {
      return this.certification.expiration_status === GRACE_PERIOD
    },
    isRenewalAvailable() {
      return this.certification.expiration_status === RENEWAL_AVAILABLE
    },
    daysToRenew() {
      if (this.isGracePeriod) {
        // certification.cert_awarded_expiration_date + 90 days from today
        const expirationDate = new Date(
          this.certification.cert_awarded_expiration_date
        )
        const gracePeriodEndDate = new Date(
          expirationDate.setDate(expirationDate.getDate() + 90)
        )
        const today = new Date()
        const timeDiff = gracePeriodEndDate - today
        const daysToRenew = Math.ceil(timeDiff / (1000 * 60 * 60 * 24))
        return daysToRenew > 0 ? daysToRenew : 0
      }
      return 0
    },
  },
  methods: {
    formatDate(date) {
      return format(new Date(date), 'MMMM dd, yyyy')
    },
    showAttemptScore() {
      if (!this.attempt) return
      this.$store.dispatch(
        'certifications/setCurrentAttemptScored',
        this.attempt
      )
      this.$store.dispatch(
        'certifications/setCurrentScoredCertification',
        this.certification
      )
    },
    handleCloseRenewalModal() {
      this.isRenewalLoading = true
      this.showRenewModal = false
      this.$emit('close-renewal-modal', this.certification.id, this.setIsRenewalLoading)
    },
    setIsRenewalLoading(value) {
      this.isRenewalLoading = value
    },
  },
}
</script>

<style lang="scss" scoped>
.certification-tile {
  $this: &;
  background: var(--mid-gray-gradient);
  border-radius: 10px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  position: relative;
  padding: 30px;
  padding-top: 50px;
  &--pt-70 {
    padding-top: 70px;
  }
  &--flex-jc-start {
    justify-content: flex-start;
  }
  &__status-banner {
    &--expired {
      $expired: &;
      position: absolute;
      top: 10px;
      right: 10px;
      & > #{$expired} {
        color: var(--carnation);
        font-size: rem(12px);
        font-style: normal;
        font-weight: 700;
        line-height: rem(14px);
      }
    }
    &--expires-soon,
    &--grace-period,
    &--renew-available {
      $expires-soon: &;
      $grace-period: &;
      $renew-available: &;
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      border-radius: 10px 10px 0 0;
      display: flex;
      align-items: center;
      gap: 5px;
      justify-content: center;
      background: none;
      & > #{$expires-soon},
      & > #{$grace-period},
      & > #{$renew-available} {
        color: var(--dark-gray-two);
        font-size: rem(12px);
        font-style: normal;
        font-weight: 450;
        line-height: rem(14px);
        & > span {
          font-weight: 700;
        }
      }
    }
    &--expires-soon {
      $expires-soon: &;
      & > #{$expires-soon} {
        background: var(--light-orage-to-orage-gradient);
      }
    }
    &--grace-period {
      $grace-period: &;
      & > #{$grace-period} {
        background: var(--orage-to-red-gradient);
      }
    }
    &--renew-available {
      $renew-available: &;
      & > #{$renew-available} {
        background: var(--light-orage-to-green-gradient);
      }
    }
    &--renew-ready {
      $renew-ready: &;
      position: absolute;
      right: 0;
      width: 100%;
      & > #{$renew-ready} {
        top: 30px;
        display: flex;
        flex-wrap: wrap;
        flex-basis: 100%;
        justify-content: center;
        color: var(--dark-brighter);
        font-size: rem(10px);
        font-style: normal;
        font-weight: 450;
        line-height: rem(12px);
      }
    }
    &--pb-15 {
      padding-bottom: 15px;
    }
  }

  &__badge {
    margin-bottom: 20px;
    display: flex;
    justify-content: center;
    width: 100%;
    height: 100%;
    &-img {
      max-width: 185px;
      max-height: 245px;
    }
    &--expired {
      height: unset;
    }
  }

  &__title {
    color: var(--white);
    font-size: rem(20px);
    font-style: normal;
    font-weight: 700;
    line-height: rem(26px);
    margin-bottom: 10px;
  }

  &__info-icon {
    color: var(--light-blue-gray);
    width: 14px;
    height: 14px;
  }

  &__awarded,
  &__expires,
  &__cpe-credits-external,
  &__cpe-credits-needed {
    font-size: rem(14px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(20px);
    color: var(--light-blue-gray);
  }

  &__cpe-credits-external {
    color: var(--positive-light);
    display: flex;
    align-items: center;
    gap: 5px;
  }

  &__cpe-credits-needed {
    color: var(--white);
    font-weight: bold;
  }

  &__separator {
    margin: 20px 0;
  }

  &__actions {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    width: 100%;
    #{$this} {
      &__renew-certification,
      &__renew-certification-spinner {
        grid-area: 2 / 1 / 3 / 3;
      }
      &__renew-certification-spinner {
        text-align: center;
      }
    }
  }
}
</style>
